// Unit decorations v2 — marker corner brackets + status bars as imperial-box-ui
// boxes in PIXEL units, living in a dedicated layer (game.main > UNIT_DECORATIONS)
// outside the world zoom. Per frame the layer owner billboards each decoration
// and scales it with the label formula (2·dist·tanHalfFov / screenH), so 1 box
// unit = 1 screen pixel at every zoom — the behavior the old CSS2D decorations
// had by nature.
//
// Colors: corners are WHITE, yellow when selected or hovered, red for a
// selected enemy. (The attack radius ring keeps the nation color — unit.js.)
import { Object3D } from 'three';
import { Box } from 'imperial-box-ui/box';

export const DECORATION_STYLE = {
	width: 56,
	height: 44,
	corner: 13,
	border: 2,
	barWidth: 56,
	barHeight: 4,
	barGap: 5,
	colors: {
		// corner states: white default (own/neutral), yellow hover, green own
		// selected, red enemy (always), blue ally (always)
		white: [1, 1, 1, 0.9],
		yellow: [1, 1, 0, 1],
		green: [0.25, 1, 0.35, 1],
		red: [1, 0.15, 0.15, 1],
		blue: [0.35, 0.55, 1, 1],
		shield: [0.25, 0.45, 1, 1],
		hull: [0.3, 0.75, 0.3, 1],
		track: [0.1, 0.1, 0.1, 0.75],
	},
};

function prepare(box, unit, renderOrder) {
	box.material.depthTest = false;
	box.material.depthWrite = false;
	box.renderOrder = renderOrder;
	box.userData.unit = unit;
	box.userData.type = 'unit';
	box.frustumCulled = false;
	return box;
}

export default class UnitDecorations {

	constructor(unit) {
		const s = DECORATION_STYLE;
		this.unit = unit;
		this.object3d = new Object3D();
		this.object3d.name = 'UNIT_DECORATIONS';
		this.object3d.userData.unit = unit;

		// invisible full-size surface so clicks/hover/XR rays hit the rectangle
		this.hitSurface = prepare(new Box({
			width: s.width + 8,
			height: s.height + 8,
			backgroundColor: [0, 0, 0, 0],
		}), unit, 1500);
		this.object3d.add(this.hitSurface);

		// four corner brackets: one box each, borders only on the outer sides
		this.corners = [
			{ x: -1, y: 1, widths: [s.border, 0, 0, s.border] }, // top left
			{ x: 1, y: 1, widths: [s.border, s.border, 0, 0] }, // top right
			{ x: -1, y: -1, widths: [0, 0, s.border, s.border] }, // bottom left
			{ x: 1, y: -1, widths: [0, s.border, s.border, 0] }, // bottom right
		].map(({ x, y, widths }) => {
			const corner = prepare(new Box({
				width: s.corner,
				height: s.corner,
				borderWidths: widths,
				borderColors: [s.colors.white, s.colors.white, s.colors.white, s.colors.white],
				backgroundColor: [0, 0, 0, 0],
			}), unit, 1501);
			corner.position.set(
				x * (s.width - s.corner) / 2,
				y * (s.height - s.corner) / 2,
				0,
			);
			this.object3d.add(corner);
			return corner;
		});

		// status bars: dark track + left-anchored fill, scaled by ratio
		const barY = -s.height / 2 - s.barGap - s.barHeight / 2;
		this.bars = {};
		[['shield', barY], ['hull', barY - s.barHeight - 2]].forEach(([key, y]) => {
			const track = prepare(new Box({
				width: s.barWidth,
				height: s.barHeight,
				backgroundColor: s.colors.track,
			}), unit, 1501);
			track.position.set(0, y, 0);
			const fill = prepare(new Box({
				width: s.barWidth - 2,
				height: s.barHeight - 2,
				backgroundColor: s.colors[key],
			}), unit, 1502);
			fill.position.set(0, y, 0);
			this.object3d.add(track);
			this.object3d.add(fill);
			this.bars[key] = fill;
		});

		this.setState('white');
	}

	// corner color key: 'white' | 'yellow' | 'green' | 'red' | 'blue'
	setState(state) {
		const color = DECORATION_STYLE.colors[state] ?? DECORATION_STYLE.colors.white;
		for (const corner of this.corners) {
			const u = corner.material.uniforms;
			u.borderTopColor.value.set(...color);
			u.borderRightColor.value.set(...color);
			u.borderBottomColor.value.set(...color);
			u.borderLeftColor.value.set(...color);
		}
	}

	setBars(shieldRatio, hullRatio) {
		this.setBar('shield', shieldRatio);
		this.setBar('hull', hullRatio);
	}

	setBar(key, ratio) {
		const width = DECORATION_STYLE.barWidth - 2;
		const clamped = Math.max(0, Math.min(1, ratio || 0));
		// a living unit never shows a fully empty bar
		const shown = clamped > 0 ? Math.max(0.04, clamped) : 0;
		const fill = this.bars[key];
		fill.visible = shown > 0;
		fill.scale.x = shown;
		fill.position.x = -width / 2 + (width * shown) / 2;
	}

	dispose() {
		this.object3d.parent?.remove(this.object3d);
		this.object3d.traverse(node => {
			node.geometry?.dispose?.();
			node.material?.dispose?.();
		});
	}
}
